import { useState, useRef, useCallback, useMemo } from "react";
import { useTranslations } from "@/hooks/use-translations";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { motion } from "motion/react";
import { ProjectHeader } from "@/components/layout/project-header";
import { Bold, ChevronDown } from "lucide-react";
import { Streamdown } from "streamdown";
import { Button } from "@/components/ui/button";

// --- Types ---
interface DocumentBlock {
	id: string;
	type: "heading" | "paragraph" | "bold-paragraph" | "checkbox-item" | "italic";
	content: string;
	checked?: boolean;
}

// --- Mock Data ---
const MOCK_DOCUMENT: DocumentBlock[] = [
	{
		id: "b1",
		type: "heading",
		content: "Strategic CFO Outsourcing LinkedIn Ad",
	},
	{ id: "b2", type: "paragraph", content: "" },
	{ id: "b3", type: "bold-paragraph", content: "Headline:" },
	{
		id: "b4",
		type: "paragraph",
		content: "Stop Guessing Your Numbers. Start Guaranteeing Your Growth.",
	},
	{ id: "b5", type: "bold-paragraph", content: "Primary Text:" },
	{
		id: "b6",
		type: "paragraph",
		content:
			"Is your finance department holding you back? While you focus on scaling, your financial blind spots could be costing you millions.",
	},
	{
		id: "b7",
		type: "paragraph",
		content:
			'At KSP Consulting, we don\'t just "do the books." We partner with high-growth startups and established enterprises to turn financial data into a competitive advantage.',
	},
	{
		id: "b8",
		type: "checkbox-item",
		content:
			"Guaranteed ROI: We commit to increasing your margin by 5% within 6 months.",
		checked: true,
	},
	{
		id: "b9",
		type: "checkbox-item",
		content:
			"The Power of Three: Replace a single hire with a complete financial command center—CFO, Business Analyst, and Junior Financier—for one transparent fee.",
		checked: true,
	},
	{
		id: "b10",
		type: "checkbox-item",
		content:
			"Institutional Grade: Get investor-ready reporting and standardized financial models that VCs trust.",
		checked: true,
	},
	{
		id: "b11",
		type: "paragraph",
		content:
			"Don't let manual spreadsheets and delayed reports stall your next round or expansion. Gain total clarity and control over your capital.",
	},
	{ id: "b12", type: "bold-paragraph", content: "Ad Creative Text Overlay:" },
	{
		id: "b13",
		type: "italic",
		content: '"We Manage Your Finances, You Manage Your Growth."',
	},
	{ id: "b14", type: "paragraph", content: "" },
	{
		id: "b15",
		type: "bold-paragraph",
		content: "Call to Action (CTA) Button:",
	},
	{ id: "b16", type: "paragraph", content: "Book Your Free Financial Audit" },
	{ id: "b17", type: "paragraph", content: "" },
	{ id: "b18", type: "bold-paragraph", content: "Headline for Lead Form:" },
	{
		id: "b19",
		type: "paragraph",
		content: "Unlock Your 5% Margin Growth Today",
	},
];

// --- Toolbar Button ---
function ToolbarButton({
	icon: Icon,
	label,
	active,
	onClick,
}: {
	icon: typeof Bold;
	label: string;
	active?: boolean;
	onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
        active
          ? "bg-bg-sunken text-text-primary"
          : "text-text-secondary hover:text-text-primary hover:bg-bg-sunken"
      }`}
    >
      <Icon size={16} strokeWidth={2} />
    </button>
  );
}

export function DocumentEditorPage() {
  const t = useTranslations("workspace");
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const locationState = location.state as {
    title?: string;
    content?: string;
  } | null;

  const documentTitle =
    locationState?.title || searchParams.get("title") || "Document";

  // Parse content from navigation state into blocks, or use mock data
  const initialBlocks = useMemo(() => {
    if (locationState?.content) {
      const lines = locationState.content.split("\n");
      return lines.map((line, i): DocumentBlock => {
        const trimmed = line.trim();
        if (!trimmed) return { id: `b${i}`, type: "paragraph", content: "" };
        if (trimmed.startsWith("# "))
          return { id: `b${i}`, type: "heading", content: trimmed.slice(2) };
        if (trimmed.startsWith("## "))
          return { id: `b${i}`, type: "heading", content: trimmed.slice(3) };
        if (trimmed.startsWith("**") && trimmed.endsWith("**"))
          return {
            id: `b${i}`,
            type: "bold-paragraph",
            content: trimmed.replace(/\*\*/g, ""),
          };
        if (trimmed.startsWith("**"))
          return {
            id: `b${i}`,
            type: "bold-paragraph",
            content: trimmed.replace(/\*\*/g, ""),
          };
        if (trimmed.startsWith("- ✅") || trimmed.startsWith("✅"))
          return {
            id: `b${i}`,
            type: "checkbox-item",
            content: trimmed.replace(/^-?\s*✅\s*/, "").replace(/\*\*/g, ""),
            checked: true,
          };
        if (trimmed.startsWith("- [ ]") || trimmed.startsWith("- [x]"))
          return {
            id: `b${i}`,
            type: "checkbox-item",
            content: trimmed.replace(/^-\s*\[.?\]\s*/, ""),
            checked: trimmed.includes("[x]"),
          };
        if (trimmed.startsWith("*") && trimmed.endsWith("*"))
          return {
            id: `b${i}`,
            type: "italic",
            content: trimmed.replace(/\*/g, ""),
          };
        return { id: `b${i}`, type: "paragraph", content: trimmed };
      });
    }
    return MOCK_DOCUMENT;
  }, [locationState]);

	const [blocks, setBlocks] = useState<DocumentBlock[]>(initialBlocks);
	const contentRef = useRef<HTMLDivElement>(null);

	const handleClose = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	return (
		<div className="flex flex-col h-screen overflow-hidden bg-bg-canvas">
			<ProjectHeader />

			{/* ── Document Tab Bar ── */}
			<div className="flex items-center justify-between px-5 py-2 bg-bg-surface border-b border-border-subtle flex-shrink-0">
				<div className="flex items-center gap-2">
					<div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-bg-canvas border border-border-subtle">
						<svg
							width="14"
							height="14"
							viewBox="0 0 16 16"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							className="text-text-primary"
						>
							<path
								d="M13.3333 2H2.66667C1.93029 2 1.33333 2.59695 1.33333 3.33333V12.6667C1.33333 13.403 1.93029 14 2.66667 14H13.3333C14.0697 14 14.6667 13.403 14.6667 12.6667V3.33333C14.6667 2.59695 14.0697 2 13.3333 2Z"
								stroke="currentColor"
								strokeWidth="1.2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M4.66667 5.33333H11.3333"
								stroke="currentColor"
								strokeWidth="1.2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M4.66667 8H11.3333"
								stroke="currentColor"
								strokeWidth="1.2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M4.66667 10.6667H8.66667"
								stroke="currentColor"
								strokeWidth="1.2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<span className="text-sm text-text-primary font-medium">
							{documentTitle}
						</span>
					</div>
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleClose}
					className="gap-1.5"
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M8.33594 16.6693C7.83594 16.6693 7.5026 16.3359 7.5026 15.8359V12.5026H4.16927C3.66927 12.5026 3.33594 12.1693 3.33594 11.6693C3.33594 11.1693 3.66927 10.8359 4.16927 10.8359H8.33594C8.83594 10.8359 9.16927 11.1693 9.16927 11.6693V15.8359C9.16927 16.3359 8.83594 16.6693 8.33594 16.6693Z"
							fill="currentColor"
						/>
						<path
							d="M15.8359 9.16927H11.6693C11.1693 9.16927 10.8359 8.83594 10.8359 8.33594V4.16927C10.8359 3.66927 11.1693 3.33594 11.6693 3.33594C12.1693 3.33594 12.5026 3.66927 12.5026 4.16927V7.5026H15.8359C16.3359 7.5026 16.6693 7.83594 16.6693 8.33594C16.6693 8.83594 16.3359 9.16927 15.8359 9.16927Z"
							fill="currentColor"
						/>
					</svg>
					Close editor
				</Button>
			</div>

			{/* ── Editor Area ── */}
			<div className="flex-1 overflow-hidden flex flex-col bg-bg-surface px-3">
				<motion.div
					initial={{ opacity: 0, y: -4 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.2, delay: 0.1 }}
					className="flex items-center justify-center gap-1 px-6 py-2 bg-bg-sunken border-b border-border-subtle flex-shrink-0 rounded-full space-x-2 text-text-primary"
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g clip-path="url(#clip0_937_7586)">
							<path
								d="M18.7791 7.05026C18.3929 6.66277 17.9337 6.35573 17.4281 6.14689C16.9224 5.93805 16.3804 5.83156 15.8333 5.83359H2.33331L5.91664 2.25026C6.07267 2.09356 6.16007 1.88129 6.1596 1.66016C6.15913 1.43902 6.07084 1.22713 5.91414 1.07109C5.75744 0.915055 5.54517 0.827658 5.32404 0.828127C5.1029 0.828596 4.89101 0.916892 4.73497 1.07359L0.609972 5.19776C0.219407 5.58844 0 6.11825 0 6.67067C0 7.2231 0.219407 7.75291 0.609972 8.14359L4.73497 12.2694C4.81234 12.347 4.90424 12.4086 5.00541 12.4507C5.10658 12.4928 5.21505 12.5145 5.32463 12.5146C5.4342 12.5148 5.54273 12.4934 5.64402 12.4516C5.74531 12.4098 5.83738 12.3485 5.91497 12.2711C5.99256 12.1937 6.05415 12.1018 6.09623 12.0007C6.1383 11.8995 6.16004 11.791 6.16019 11.6814C6.16035 11.5719 6.13892 11.4633 6.09713 11.362C6.05534 11.2607 5.99401 11.1687 5.91664 11.0911L2.32331 7.50026H15.8333C16.4963 7.50026 17.1322 7.76365 17.6011 8.23249C18.0699 8.70133 18.3333 9.33722 18.3333 10.0003V15.8336C18.3333 16.4966 18.0699 17.1325 17.6011 17.6014C17.1322 18.0702 16.4963 18.3336 15.8333 18.3336H4.16664C3.94562 18.3336 3.73366 18.4214 3.57738 18.5777C3.4211 18.7339 3.33331 18.9459 3.33331 19.1669C3.33331 19.3879 3.4211 19.5999 3.57738 19.7562C3.73366 19.9125 3.94562 20.0003 4.16664 20.0003H15.8333C16.938 19.9989 17.997 19.5595 18.7781 18.7784C19.5592 17.9973 19.9986 16.9383 20 15.8336V10.0003C20.0018 9.45222 19.8949 8.90926 19.6853 8.40287C19.4757 7.89647 19.1677 7.43672 18.7791 7.05026Z"
								fill="currentColor"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_7586">
								<rect width="20" height="20" fill="white" />
							</clipPath>
						</defs>
					</svg>

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g clip-path="url(#clip0_937_7588)">
							<path
								d="M14.0833 12.2712C14.2396 12.4274 14.4515 12.5152 14.6725 12.5152C14.8935 12.5152 15.1054 12.4274 15.2617 12.2712L19.3858 8.1462C19.5793 7.95274 19.7329 7.72305 19.8376 7.47025C19.9423 7.21745 19.9962 6.9465 19.9962 6.67287C19.9962 6.39923 19.9423 6.12828 19.8376 5.87548C19.7329 5.62269 19.5793 5.393 19.3858 5.19953L15.2608 1.07537C15.1024 0.931678 14.8947 0.854431 14.6809 0.859621C14.467 0.86481 14.2633 0.952038 14.112 1.10324C13.9607 1.25445 13.8733 1.45806 13.868 1.6719C13.8627 1.88575 13.9398 2.09346 14.0833 2.25203L17.6667 5.83537H4.16667C3.0616 5.83537 2.00179 6.27435 1.22039 7.05576C0.438987 7.83716 0 8.89697 0 10.002L0 15.8354C0.00132321 16.94 0.440735 17.9991 1.22185 18.7802C2.00296 19.5613 3.062 20.0007 4.16667 20.002H15.8333C16.0543 20.002 16.2663 19.9142 16.4226 19.758C16.5789 19.6017 16.6667 19.3897 16.6667 19.1687C16.6667 18.9477 16.5789 18.7357 16.4226 18.5794C16.2663 18.4232 16.0543 18.3354 15.8333 18.3354H4.16667C3.50363 18.3354 2.86774 18.072 2.3989 17.6031C1.93006 17.1343 1.66667 16.4984 1.66667 15.8354V10.002C1.66667 9.33899 1.93006 8.70311 2.3989 8.23427C2.86774 7.76543 3.50363 7.50203 4.16667 7.50203H17.6767L14.0833 11.0929C13.9271 11.2491 13.8393 11.4611 13.8393 11.682C13.8393 11.903 13.9271 12.1149 14.0833 12.2712Z"
								fill="currentColor"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_7588">
								<rect width="20" height="20" fill="white" />
							</clipPath>
						</defs>
					</svg>

					<div className="w-px h-5 bg-border-subtle mx-1.5" />

					{/* Text style dropdown */}
					<button className="flex items-center gap-1 px-2.5 py-1 rounded-xl text-xl text-text-primary hover:bg-bg-sunken transition-colors cursor-pointer">
						Normal text
						<ChevronDown size={14} />
					</button>

					<div className="w-px h-5 bg-border-subtle mx-3" />

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g clip-path="url(#clip0_937_7584)">
							<path
								d="M14.9617 8.88585C15.508 8.00321 15.8085 6.99054 15.8318 5.95275C15.8552 4.91496 15.6007 3.88979 15.0946 2.98345C14.5885 2.07712 13.8493 1.32258 12.9535 0.798016C12.0578 0.273449 11.0381 -0.00206655 10 1.16705e-05H4.16667C3.72464 1.16705e-05 3.30072 0.175606 2.98816 0.488167C2.67559 0.800728 2.5 1.22465 2.5 1.66668V18.3333C2.5 18.7754 2.67559 19.1993 2.98816 19.5119C3.30072 19.8244 3.72464 20 4.16667 20H12.5C13.8279 20.0014 15.1164 19.5494 16.1523 18.7187C17.1882 17.888 17.9095 16.7285 18.1966 15.432C18.4838 14.1356 18.3196 12.78 17.7313 11.5896C17.1431 10.3991 16.1659 9.44527 14.9617 8.88585ZM5.83333 3.33334H10C10.663 3.33334 11.2989 3.59674 11.7678 4.06558C12.2366 4.53442 12.5 5.1703 12.5 5.83334C12.5 6.49639 12.2366 7.13227 11.7678 7.60111C11.2989 8.06995 10.663 8.33335 10 8.33335H5.83333V3.33334ZM12.5 16.6667H5.83333V11.6667H12.5C13.163 11.6667 13.7989 11.9301 14.2678 12.3989C14.7366 12.8678 15 13.5036 15 14.1667C15 14.8297 14.7366 15.4656 14.2678 15.9344C13.7989 16.4033 13.163 16.6667 12.5 16.6667Z"
								fill="currentColor"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_7584">
								<rect width="20" height="20" fill="white" />
							</clipPath>
						</defs>
					</svg>

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g clip-path="url(#clip0_937_7579)">
							<path
								d="M10.0026 16.6667C11.7701 16.6647 13.4646 15.9617 14.7145 14.7119C15.9643 13.462 16.6673 11.7675 16.6693 10V0.833333C16.6693 0.61232 16.5815 0.400358 16.4252 0.244078C16.2689 0.0877974 16.057 0 15.8359 0C15.6149 0 15.403 0.0877974 15.2467 0.244078C15.0904 0.400358 15.0026 0.61232 15.0026 0.833333V10C15.0026 11.3261 14.4758 12.5979 13.5381 13.5355C12.6005 14.4732 11.3287 15 10.0026 15C8.67652 15 7.40475 14.4732 6.46707 13.5355C5.52939 12.5979 5.0026 11.3261 5.0026 10V0.833333C5.0026 0.61232 4.91481 0.400358 4.75853 0.244078C4.60225 0.0877974 4.39028 0 4.16927 0C3.94826 0 3.7363 0.0877974 3.58002 0.244078C3.42374 0.400358 3.33594 0.61232 3.33594 0.833333V10C3.33792 11.7675 4.04094 13.462 5.29075 14.7119C6.54056 15.9617 8.2351 16.6647 10.0026 16.6667Z"
								fill="currentColor"
							/>
							<path
								d="M19.1667 18.3359H0.833333C0.61232 18.3359 0.400358 18.4237 0.244078 18.58C0.0877974 18.7363 0 18.9483 0 19.1693C0 19.3903 0.0877974 19.6022 0.244078 19.7585C0.400358 19.9148 0.61232 20.0026 0.833333 20.0026H19.1667C19.3877 20.0026 19.5996 19.9148 19.7559 19.7585C19.9122 19.6022 20 19.3903 20 19.1693C20 18.9483 19.9122 18.7363 19.7559 18.58C19.5996 18.4237 19.3877 18.3359 19.1667 18.3359Z"
								fill="currentColor"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_7579">
								<rect width="20" height="20" fill="white" />
							</clipPath>
						</defs>
					</svg>

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M16.6667 0H5.83333C5.61232 0 5.40036 0.0877974 5.24408 0.244078C5.0878 0.400358 5 0.61232 5 0.833333C5 1.05435 5.0878 1.26631 5.24408 1.42259C5.40036 1.57887 5.61232 1.66667 5.83333 1.66667H10.295L8.0225 18.3333H3.33333C3.11232 18.3333 2.90036 18.4211 2.74408 18.5774C2.5878 18.7337 2.5 18.9457 2.5 19.1667C2.5 19.3877 2.5878 19.5996 2.74408 19.7559C2.90036 19.9122 3.11232 20 3.33333 20H14.1667C14.3877 20 14.5996 19.9122 14.7559 19.7559C14.9122 19.5996 15 19.3877 15 19.1667C15 18.9457 14.9122 18.7337 14.7559 18.5774C14.5996 18.4211 14.3877 18.3333 14.1667 18.3333H9.705L11.9775 1.66667H16.6667C16.8877 1.66667 17.0996 1.57887 17.2559 1.42259C17.4122 1.26631 17.5 1.05435 17.5 0.833333C17.5 0.61232 17.4122 0.400358 17.2559 0.244078C17.0996 0.0877974 16.8877 0 16.6667 0Z"
							fill="currentColor"
						/>
					</svg>

					<div className="w-px h-5 bg-border-subtle mx-1.5" />

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M5.83333 5.0026H19.1667C19.3877 5.0026 19.5996 4.91481 19.7559 4.75853C19.9122 4.60225 20 4.39028 20 4.16927C20 3.94826 19.9122 3.7363 19.7559 3.58002C19.5996 3.42374 19.3877 3.33594 19.1667 3.33594H5.83333C5.61232 3.33594 5.40036 3.42374 5.24408 3.58002C5.0878 3.7363 5 3.94826 5 4.16927C5 4.39028 5.0878 4.60225 5.24408 4.75853C5.40036 4.91481 5.61232 5.0026 5.83333 5.0026Z"
							fill="currentColor"
						/>
						<path
							d="M19.1667 9.16406H5.83333C5.61232 9.16406 5.40036 9.25186 5.24408 9.40814C5.0878 9.56442 5 9.77638 5 9.9974C5 10.2184 5.0878 10.4304 5.24408 10.5867C5.40036 10.7429 5.61232 10.8307 5.83333 10.8307H19.1667C19.3877 10.8307 19.5996 10.7429 19.7559 10.5867C19.9122 10.4304 20 10.2184 20 9.9974C20 9.77638 19.9122 9.56442 19.7559 9.40814C19.5996 9.25186 19.3877 9.16406 19.1667 9.16406Z"
							fill="currentColor"
						/>
						<path
							d="M19.1667 15H5.83333C5.61232 15 5.40036 15.0878 5.24408 15.2441C5.0878 15.4004 5 15.6123 5 15.8333C5 16.0543 5.0878 16.2663 5.24408 16.4226C5.40036 16.5789 5.61232 16.6667 5.83333 16.6667H19.1667C19.3877 16.6667 19.5996 16.5789 19.7559 16.4226C19.9122 16.2663 20 16.0543 20 15.8333C20 15.6123 19.9122 15.4004 19.7559 15.2441C19.5996 15.0878 19.3877 15 19.1667 15Z"
							fill="currentColor"
						/>
						<path
							d="M1.66667 5.83333C2.58714 5.83333 3.33333 5.08714 3.33333 4.16667C3.33333 3.24619 2.58714 2.5 1.66667 2.5C0.746192 2.5 0 3.24619 0 4.16667C0 5.08714 0.746192 5.83333 1.66667 5.83333Z"
							fill="currentColor"
						/>
						<path
							d="M1.66667 11.6693C2.58714 11.6693 3.33333 10.9231 3.33333 10.0026C3.33333 9.08213 2.58714 8.33594 1.66667 8.33594C0.746192 8.33594 0 9.08213 0 10.0026C0 10.9231 0.746192 11.6693 1.66667 11.6693Z"
							fill="currentColor"
						/>
						<path
							d="M1.66667 17.4974C2.58714 17.4974 3.33333 16.7512 3.33333 15.8307C3.33333 14.9103 2.58714 14.1641 1.66667 14.1641C0.746192 14.1641 0 14.9103 0 15.8307C0 16.7512 0.746192 17.4974 1.66667 17.4974Z"
							fill="currentColor"
						/>
					</svg>

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g clip-path="url(#clip0_937_7599)">
							<path
								d="M1.27298 2.60463L1.57509 2.37053V4.67838C1.57509 5.10901 1.92419 5.45811 2.35481 5.45811C2.78544 5.45811 3.13454 5.10901 3.13454 4.67838V0.779711C3.13454 0.482172 2.96516 0.210492 2.69802 0.0795935C2.43079 -0.051383 2.11235 -0.018844 1.8772 0.163422L0.317742 1.37198C-0.0226092 1.63577 -0.0847186 2.12557 0.17907 2.46596C0.44282 2.80627 0.932547 2.86842 1.27298 2.60463Z"
								fill="currentColor"
							/>
							<path
								d="M7.10785 3.51258H19.1936C19.6243 3.51258 19.9734 3.16348 19.9734 2.73285C19.9734 2.30223 19.6243 1.95312 19.1936 1.95312H7.10785C6.67723 1.95312 6.32812 2.30223 6.32812 2.73285C6.32812 3.16352 6.67723 3.51258 7.10785 3.51258Z"
								fill="currentColor"
							/>
							<path
								d="M1.99304 14.1094C1.98835 14.1094 1.98382 14.11 1.97921 14.1101C1.97452 14.11 1.97003 14.1094 1.96538 14.1094C1.27902 14.1094 0.636243 14.4605 0.287923 15.0259C0.0620244 15.3925 0.176126 15.8729 0.542728 16.0987C0.670267 16.1773 0.811478 16.2147 0.951048 16.2147C1.21269 16.2147 1.46831 16.083 1.61562 15.8439C1.68117 15.7375 1.81847 15.6688 1.96546 15.6688C1.97015 15.6688 1.97468 15.6682 1.97929 15.6681C1.98398 15.6682 1.98847 15.6688 1.99312 15.6688C2.20441 15.6688 2.383 15.8474 2.383 16.0587C2.383 16.1191 2.21452 16.289 2.04874 16.3734L2.05577 16.3699L2.05601 16.3705C1.88261 16.4541 1.73933 16.6009 1.6655 16.795C1.51238 17.1975 1.71449 17.6479 2.11695 17.8011C2.19909 17.8323 2.36488 17.9489 2.38296 18.008C2.38296 18.2193 2.20441 18.3979 1.99308 18.3979C1.94273 18.3979 1.89363 18.4031 1.84593 18.4122C1.7555 18.3776 1.66851 18.3088 1.61554 18.2228C1.3896 17.8562 0.90929 17.742 0.542688 17.968C0.176048 18.1939 0.0619854 18.6742 0.287884 19.0408C0.65097 19.6302 1.29374 19.9963 1.96534 19.9963C2.06921 19.9963 2.16808 19.9756 2.2587 19.9388C3.20831 19.8088 3.94238 18.9927 3.94238 18.008C3.94238 17.6217 3.80499 17.3008 3.6107 17.0444C3.79902 16.7861 3.94238 16.4586 3.94238 16.0587C3.94238 14.9838 3.06788 14.1094 1.99304 14.1094Z"
								fill="currentColor"
							/>
							<path
								d="M19.1936 9.20312H7.10785C6.67723 9.20312 6.32812 9.55223 6.32812 9.98285C6.32812 10.4135 6.67723 10.7626 7.10785 10.7626H19.1936C19.6243 10.7626 19.9734 10.4135 19.9734 9.98285C19.9734 9.55219 19.6243 9.20312 19.1936 9.20312Z"
								fill="currentColor"
							/>
							<path
								d="M19.1936 16.2578H7.10785C6.67723 16.2578 6.32812 16.6069 6.32812 17.0375C6.32812 17.4682 6.67723 17.8173 7.10785 17.8173H19.1936C19.6243 17.8173 19.9734 17.4682 19.9734 17.0375C19.9734 16.6069 19.6243 16.2578 19.1936 16.2578Z"
								fill="currentColor"
							/>
							<path
								d="M3.21251 11.187H2.65403C3.79302 9.89586 3.942 9.25938 3.942 8.84777C3.942 7.77293 3.06755 6.89844 1.99267 6.89844C1.98798 6.89844 1.98345 6.89906 1.97884 6.89914C1.97415 6.89906 1.96966 6.89844 1.96501 6.89844C0.890166 6.89844 0.0156739 7.77289 0.0156739 8.84777C0.0156739 9.2784 0.364775 9.6275 0.7954 9.6275C1.22603 9.6275 1.57513 9.2784 1.57513 8.84777C1.57513 8.63648 1.75368 8.45793 1.96501 8.45793C1.9697 8.45793 1.97423 8.4573 1.97884 8.45723C1.98353 8.4573 1.98802 8.45793 1.99267 8.45793C2.2013 8.45793 2.3779 8.63188 2.38247 8.83961C2.37485 8.87973 2.27884 9.28137 1.36138 10.2931C0.8179 10.8925 0.277119 11.3841 0.271885 11.3888C0.0329786 11.6053 -0.0481152 11.9464 0.0678614 12.2472C0.183838 12.548 0.472979 12.7464 0.795361 12.7464H3.21251C3.64313 12.7464 3.99224 12.3973 3.99224 11.9667C3.99224 11.5361 3.64317 11.187 3.21251 11.187Z"
								fill="currentColor"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_7599">
								<rect width="20" height="20" fill="white" />
							</clipPath>
						</defs>
					</svg>

					<div className="w-px h-5 bg-border-subtle mx-3" />

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g clip-path="url(#clip0_937_7595)">
							<path
								d="M11.5369 14.3884L8.81857 17.1067C8.02934 17.8749 6.96942 18.3014 5.86811 18.294C4.76681 18.2866 3.7127 17.8459 2.93383 17.0673C2.15497 16.2886 1.71398 15.2347 1.70629 14.1334C1.6986 13.0321 2.12482 11.972 2.89274 11.1826L5.61107 8.46173C5.76733 8.30536 5.85507 8.09333 5.85499 7.87227C5.85491 7.65121 5.76702 7.43924 5.61066 7.28298C5.45429 7.12672 5.24225 7.03898 5.0212 7.03906C4.80014 7.03914 4.58816 7.12703 4.43191 7.2834L1.71441 10.0042C0.6163 11.1029 -0.000390527 12.5928 1.85542e-07 14.1461C0.000390898 15.6995 0.617831 17.189 1.71649 18.2871C2.81515 19.3853 4.30503 20.0019 5.85838 20.0016C7.41173 20.0012 8.9013 19.3837 9.99941 18.2851L12.7177 15.5667C12.8695 15.4096 12.9535 15.1991 12.9516 14.9806C12.9497 14.7621 12.8621 14.5531 12.7076 14.3985C12.5531 14.244 12.3441 14.1564 12.1256 14.1545C11.9071 14.1526 11.6966 14.2366 11.5394 14.3884H11.5369Z"
								fill="currentColor"
							/>
							<path
								d="M18.2867 1.71752C17.7445 1.17153 17.0992 0.738586 16.3884 0.443767C15.6776 0.148948 14.9154 -0.00188038 14.1459 2.0534e-05C13.3768 -0.00202257 12.6149 0.148419 11.9043 0.442647C11.1937 0.736874 10.5484 1.16905 10.0059 1.71419L7.2834 4.43335C7.12703 4.58961 7.03914 4.80158 7.03906 5.02264C7.03898 5.2437 7.12672 5.45574 7.28298 5.6121C7.43924 5.76847 7.65121 5.85636 7.87227 5.85644C8.09333 5.85652 8.30536 5.76878 8.46173 5.61252L11.1826 2.89419C11.5705 2.50389 12.0321 2.19444 12.5404 1.98374C13.0488 1.77303 13.5939 1.66527 14.1442 1.66669C14.9728 1.66696 15.7827 1.91288 16.4715 2.37335C17.1603 2.83383 17.6971 3.48818 18.0141 4.2537C18.3311 5.01922 18.414 5.86154 18.2523 6.67417C18.0907 7.4868 17.6917 8.23326 17.1059 8.81919L14.3876 11.5375C14.2312 11.6939 14.1434 11.906 14.1434 12.1271C14.1434 12.3482 14.2312 12.5603 14.3876 12.7167C14.5439 12.8731 14.756 12.9609 14.9771 12.9609C15.1983 12.9609 15.4104 12.8731 15.5667 12.7167L18.2851 10C19.3818 8.90093 19.9978 7.41177 19.9981 5.85912C19.9985 4.30646 19.383 2.81705 18.2867 1.71752Z"
								fill="currentColor"
							/>
							<path
								d="M11.9107 6.91361L6.91066 11.9136C6.83107 11.9905 6.76758 12.0824 6.72391 12.1841C6.68023 12.2858 6.65724 12.3951 6.65628 12.5058C6.65532 12.6164 6.6764 12.7262 6.71831 12.8286C6.76021 12.931 6.82208 13.024 6.90033 13.1023C6.97857 13.1805 7.07161 13.2424 7.17403 13.2843C7.27644 13.3262 7.38617 13.3473 7.49682 13.3463C7.60747 13.3454 7.71682 13.3224 7.81849 13.2787C7.92016 13.235 8.01212 13.1715 8.08899 13.0919L13.089 8.09195C13.2408 7.93478 13.3248 7.72428 13.3229 7.50578C13.321 7.28728 13.2333 7.07827 13.0788 6.92377C12.9243 6.76926 12.7153 6.68162 12.4968 6.67972C12.2783 6.67782 12.0678 6.76182 11.9107 6.91361Z"
								fill="currentColor"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_7595">
								<rect width="20" height="20" fill="white" />
							</clipPath>
						</defs>
					</svg>

					<svg
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g clip-path="url(#clip0_937_7592)">
							<path
								d="M15.8333 0H4.16667C3.062 0.00132321 2.00296 0.440735 1.22185 1.22185C0.440735 2.00296 0.00132321 3.062 0 4.16667L0 15.8333C0.00132321 16.938 0.440735 17.997 1.22185 18.7782C2.00296 19.5593 3.062 19.9987 4.16667 20H15.8333C16.938 19.9987 17.997 19.5593 18.7782 18.7782C19.5593 17.997 19.9987 16.938 20 15.8333V4.16667C19.9987 3.062 19.5593 2.00296 18.7782 1.22185C17.997 0.440735 16.938 0.00132321 15.8333 0ZM4.16667 1.66667H15.8333C16.4964 1.66667 17.1323 1.93006 17.6011 2.3989C18.0699 2.86774 18.3333 3.50363 18.3333 4.16667V15.8333C18.3319 16.2046 18.2464 16.5707 18.0833 16.9042L10.4475 9.26833C10.0606 8.88131 9.6012 8.57429 9.0956 8.36483C8.59001 8.15537 8.0481 8.04756 7.50083 8.04756C6.95357 8.04756 6.41166 8.15537 5.90606 8.36483C5.40047 8.57429 4.94109 8.88131 4.55417 9.26833L1.66667 12.155V4.16667C1.66667 3.50363 1.93006 2.86774 2.3989 2.3989C2.86774 1.93006 3.50363 1.66667 4.16667 1.66667ZM4.16667 18.3333C3.50363 18.3333 2.86774 18.0699 2.3989 17.6011C1.93006 17.1323 1.66667 16.4964 1.66667 15.8333V14.5117L5.73167 10.4467C5.96383 10.2144 6.2395 10.0301 6.54292 9.90432C6.84634 9.77858 7.17156 9.71387 7.5 9.71387C7.82844 9.71387 8.15366 9.77858 8.45708 9.90432C8.7605 10.0301 9.03617 10.2144 9.26833 10.4467L16.9042 18.0833C16.5707 18.2464 16.2046 18.3319 15.8333 18.3333H4.16667Z"
								fill="currentColor"
							/>
							<path
								d="M13.3307 8.7474C13.9076 8.7474 14.4715 8.57634 14.9511 8.25585C15.4308 7.93536 15.8046 7.47984 16.0254 6.94689C16.2461 6.41394 16.3039 5.8275 16.1914 5.26172C16.0788 4.69594 15.801 4.17624 15.3931 3.76834C14.9852 3.36043 14.4655 3.08265 13.8997 2.97011C13.334 2.85757 12.7475 2.91533 12.2146 3.13608C11.6816 3.35684 11.2261 3.73067 10.9056 4.21032C10.5851 4.68996 10.4141 5.25387 10.4141 5.83073C10.4141 6.60428 10.7214 7.34614 11.2683 7.89313C11.8153 8.44011 12.5572 8.7474 13.3307 8.7474ZM13.3307 4.58073C13.578 4.58073 13.8196 4.65404 14.0252 4.79139C14.2308 4.92875 14.391 5.12397 14.4856 5.35238C14.5802 5.58078 14.6049 5.83212 14.5567 6.07459C14.5085 6.31707 14.3894 6.5398 14.2146 6.71461C14.0398 6.88943 13.8171 7.00848 13.5746 7.05671C13.3321 7.10494 13.0808 7.08019 12.8524 6.98558C12.624 6.89097 12.4287 6.73075 12.2914 6.52519C12.154 6.31963 12.0807 6.07796 12.0807 5.83073C12.0807 5.49921 12.2124 5.18127 12.4468 4.94685C12.6813 4.71243 12.9992 4.58073 13.3307 4.58073Z"
								fill="currentColor"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_7592">
								<rect width="20" height="20" fill="white" />
							</clipPath>
						</defs>
					</svg>

					<div className="w-px h-5 bg-border-subtle mx-1.5" />

					<Button variant="ghost" size="icon-sm">
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M15.5897 6.84025C15.5123 6.76214 15.4201 6.70015 15.3185 6.65784C15.217 6.61553 15.1081 6.59375 14.9981 6.59375C14.888 6.59375 14.7791 6.61553 14.6776 6.65784C14.576 6.70015 14.4839 6.76214 14.4064 6.84025L10.5897 10.6569C10.5123 10.735 10.4201 10.797 10.3185 10.8393C10.217 10.8816 10.1081 10.9034 9.99806 10.9034C9.88805 10.9034 9.77912 10.8816 9.67758 10.8393C9.57603 10.797 9.48386 10.735 9.40639 10.6569L5.58972 6.84025C5.51225 6.76214 5.42009 6.70015 5.31854 6.65784C5.21699 6.61553 5.10807 6.59375 4.99806 6.59375C4.88805 6.59375 4.77912 6.61553 4.67758 6.65784C4.57603 6.70015 4.48386 6.76214 4.40639 6.84025C4.25118 6.99638 4.16406 7.20759 4.16406 7.42775C4.16406 7.6479 4.25118 7.85911 4.40639 8.01525L8.23139 11.8402C8.70014 12.3084 9.33555 12.5714 9.99806 12.5714C10.6606 12.5714 11.296 12.3084 11.7647 11.8402L15.5897 8.01525C15.7449 7.85911 15.8321 7.6479 15.8321 7.42775C15.8321 7.20759 15.7449 6.99638 15.5897 6.84025Z"
								fill="currentColor"
							/>
						</svg>
					</Button>
				</motion.div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            ref={contentRef}
            className="max-w-[780px] mx-auto bg-bg-surface my-6 rounded-2xl shadow-sm border border-border-subtle px-12 py-10"
            style={{
              minHeight: "calc(100vh - 220px)",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {locationState?.content ? (
              <div className="text-sm text-text-secondary leading-relaxed">
                <Streamdown>{locationState.content}</Streamdown>
              </div>
            ) : (
              blocks.map((block) => {
                switch (block.type) {
                  case "heading":
                    return (
                      <h1
                        key={block.id}
                        className="text-2xl font-bold text-text-primary mb-4 outline-none"
                        contentEditable
                        suppressContentEditableWarning
                      >
                        {block.content}
                      </h1>
                    );
                  case "bold-paragraph":
                    return (
                      <p
                        key={block.id}
                        className="text-sm font-bold text-text-primary mt-4 mb-1 outline-none"
                        contentEditable
                        suppressContentEditableWarning
                      >
                        {block.content}
                      </p>
                    );
                  case "checkbox-item":
                    return (
                      <div
                        key={block.id}
                        className="flex items-start gap-2 mb-2"
                      >
                        <span className="mt-0.5 text-success text-base flex-shrink-0">
                          ✅
                        </span>
                        <p
                          className="text-sm text-text-secondary leading-relaxed outline-none"
                          contentEditable
                          suppressContentEditableWarning
                        >
                          {block.content}
                        </p>
                      </div>
                    );
                  case "italic":
                    return (
                      <p
                        key={block.id}
                        className="text-sm italic text-text-secondary mb-1 outline-none"
                        contentEditable
                        suppressContentEditableWarning
                      >
                        {block.content}
                      </p>
                    );
                  case "paragraph":
                  default:
                    return block.content ? (
                      <p
                        key={block.id}
                        className="text-sm text-text-secondary leading-relaxed mb-1 outline-none"
                        contentEditable
                        suppressContentEditableWarning
                      >
                        {block.content}
                      </p>
                    ) : (
                      <div key={block.id} className="h-4" />
                    );
                }
              })
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
